import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import RawMaterialService from '../services/RawMaterialService';
import { isEmpty } from 'lodash';
import FinishedGoodService from '../services/FinishedGoodService';

const initialState = {
  data: [],
  datas: [],
  parent_datas: [],
  formError: [],
  pagination: {
    page: 0,
    per_page: 10,
    total: 10,
    totalPages: 10,
  },
  error: null,
  isSuccess: false,
  loading: false,
  submitLoading: false,
  isOpenRawMaterialDialog: false
};

export const fetchRawMaterialList = createAsyncThunk(
  'rawMaterial/fetchRawMaterialList',
  async ({ perPage, page, keyword, type = '' }) => {
    try {
      const resp = await RawMaterialService.List({ perPage, page, keyword, type });
      return resp;
    } catch (e) {
      return e.response;
    }
  },
);

export const createRawMaterial = createAsyncThunk('rawMaterial/createRawMaterial', async (data, { rejectWithValue }) => {
  try {
    const resp = await RawMaterialService.Create(data);
    return resp;
  } catch (error) {
    return rejectWithValue(error.response);
  }
});

export const updateRawMaterial = createAsyncThunk('rawMaterial/updateRawMaterial',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await RawMaterialService.Put(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

export const deleteRawMaterial = createAsyncThunk('rawMaterial/deleteRawMaterial', async (id) => {
  try {
    const resp = await RawMaterialService.Delt(id);
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const fetchRawMaterialDetail = createAsyncThunk('rawMaterial/detail', async (id) => {
  try {
    const resp = await RawMaterialService.Detail(id);
    return resp;
  } catch (error) {
    return error.response;
    
  }
})

export const getAllRawMaterial = createAsyncThunk('rawMaterial/all', async () => {
  try {
    const resp = await RawMaterialService.All();
    return resp;
  } catch (e) {
    return e.response;
  }
})

export const fetchRawMaterialInventories = createAsyncThunk('rawMaterial/inventories', async (warehouseId) => {
  try {
    const resp = await RawMaterialService.Inventories(warehouseId);
    return resp;
  } catch (e) {
    return e.response;
  }
})

export const updateRawMaterialActivation = createAsyncThunk('rawMaterial/activate', async ({id, data}) => {
  try {
    const resp = await RawMaterialService.Activation(id, data);
    return resp;
  } catch (error) {
    return error.response
  }
})

export const updateRawMaterialOrderAvailable = createAsyncThunk('rawMaterial/order_available', async ({id, data}) => {
  try {
    const resp = await RawMaterialService.OrderAvailable(id, data);
    return resp;
  } catch (error) {
    return error.response
  }
})

export const updatePrice = createAsyncThunk(
  'rawMaterial/changePrice',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await FinishedGoodService.ChangePrice(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const rawMaterialSlice = createSlice({
  name: 'rawMaterial',
  initialState: initialState,
  reducers: {
    openRawMaterialDialog: (state, action) => {
      state.isOpenRawMaterialDialog = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRawMaterialList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchRawMaterialList.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = [...action.payload?.data.data?.rawMaterialList];
          state.pagination = action.payload?.data.data?.pagination;
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchRawMaterialList.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.loading = false;
      })
      .addCase(createRawMaterial.pending, (state, action) => {
        state.submitLoading = true;
        state.formError = [];
        state.isSuccess = false;
      })
      .addCase(createRawMaterial.fulfilled, (state, action) => {
        state.datas.unshift(action.payload.data.data);
        toast.success(action.payload?.message);
        state.pagination = {
          ...state.pagination,
          total: state.pagination.total + 1,
        };
        state.submitLoading = false;
        state.isSuccess = true;
      })
      .addCase(createRawMaterial.rejected, (state, action) => {
        const errors = action.payload?.data.errors;
        state.submitLoading = false;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(updateRawMaterial.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateRawMaterial.fulfilled, (state, action) => {
        const index = state.datas.findIndex((client) => client.id === action.payload.data.data.id);
        state.datas[index] = {
          ...state[index],
          ...action.payload.data.data,
        };
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isSuccess = true;
      })
      .addCase(updateRawMaterial.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(deleteRawMaterial.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(deleteRawMaterial.fulfilled, (state, action) => {
        const deletedId = action.meta.arg;
        state.datas = state.datas.filter(item => item.id !== deletedId);
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isOpenRawMaterialDialog = false;
        state.pagination = {
          ...state.pagination,
          total: state.pagination.total > 0 ? state.pagination.total - 1 : 0,
        };
      })
      .addCase(deleteRawMaterial.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.submitLoading = false;
      })
      .addCase(getAllRawMaterial.pending, (state, action) => {
        state.info = '';
        // state.submitLoading = true;
      })
      .addCase(getAllRawMaterial.fulfilled, (state, action) => {
        if(action.payload?.status === 200) {
          state.datas = [...action.payload?.data?.data];
        }else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(getAllRawMaterial.rejected, (state, action) => {
        state.error = action.payload?.data.message;
        state.loading = false;
        toast.error(state.error);
      })
      .addCase(fetchRawMaterialInventories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRawMaterialInventories.fulfilled, (state, action) => {
        if(action.payload?.status === 200) {
          // Map inventory data to value/label format for CustomSelect
          state.datas = action.payload?.data?.data.map(item => ({
            value: item.raw_material_id,
            label: `${item.name} (${item.sku})`,
            name: item.name,
            sku: item.sku,
            quantity: item.quantity,
            available_quantity: item.available_quantity,
            unit: item.unit,
            unit_cost: item.unit_cost
          }));
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchRawMaterialInventories.rejected, (state, action) => {
        state.error = action.payload?.data.message;
        state.loading = false;
        toast.error(state.error);
      })
      .addCase(fetchRawMaterialDetail.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchRawMaterialDetail.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.data = action.payload?.data?.data;
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchRawMaterialDetail.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.loading = false;
      })
      .addCase(updateRawMaterialActivation.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(updateRawMaterialActivation.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.info = action.payload?.data.message;
          const index = state.datas.findIndex(
            (client) => client.id === action.payload.data?.data?.rawMaterial?.id,
          );
          state.datas[index] = {
            ...state[index],
            ...action.payload.data?.data?.rawMaterial,
          };
          toast.success(action.payload?.data.message);
        } else {
          toast.error(action.payload?.data.message);
        }
        state.formError = action.payload?.data.errors;
        state.submitLoading = false;
      })
      .addCase(updateRawMaterialActivation.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(updateRawMaterialOrderAvailable.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(updateRawMaterialOrderAvailable.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.info = action.payload?.data.message;
          const index = state.datas.findIndex(
            (client) => client.id === action.payload.data?.data?.rawMaterial?.id,
          );
          state.datas[index] = {
            ...state[index],
            ...action.payload.data?.data?.rawMaterial,
          };
          toast.success(action.payload?.data.message);
        } else {
          toast.error(action.payload?.data.message);
        }
        state.formError = action.payload?.data.errors;
        state.submitLoading = false;
      })
      .addCase(updateRawMaterialOrderAvailable.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      }).addCase(updatePrice.pending, (state, action) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(updatePrice.fulfilled, (state, action) => {
        const updatedData = action.payload.data.data;
        const index = state.datas.findIndex((item) => item.sku === updatedData.sku);
      
        if (index !== -1) {
          state.datas[index].unit_price = updatedData.unit_price;
        }
      
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isSuccess = true;
      })
      .addCase(updatePrice.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      });
  },
});

const { reducer } = rawMaterialSlice;

export const { openRawMaterialDialog } = rawMaterialSlice.actions;

export const rawMaterialSelector = (state) => state?.rawMaterial;

export default reducer;
